import React, { useState } from "react";
import styled from "styled-components";
import {
  Cost,
  DrinkCostTypes,
  FoodCostTypes,
  SpaceCostTypes
} from "sweet-global";
import MoneyIcon from "sweet-web/components/icons/MoneyIcon";
import TrashIcon from "sweet-web/components/icons/TrashIcon";
import { ItemLabel, ItemTitle } from "sweet-web/components/universal/Text";
import { colors } from "sweet-web/lib/styles";
import { globalInputStyles } from "./index";

/*
Line Input
Normal input with extra title, value and onchange
*/

const Outer = styled.div`
  width: auto;
  margin: 10px 0;
`;

const Wrapper = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  position: relative;
  width: auto;
  background-color: ${colors.white};
  border-radius: 5px;
  box-shadow: ${props =>
    props.active ? `rgba(88, 29, 208, 0.08) 0 5px 10px 0` : "none"};
  border: 1px solid ${colors.lightGray};
  &:hover {
    box-shadow: rgba(88, 29, 208, 0.08) 0 5px 10px 0;
  }
  height: 40px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex: 1;
  height: 100%;
`;

const MoneySign = styled.div<{ focus: boolean; amount: number }>`
  position: absolute;
  left: 15px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600px;
  color: ${props => (props.amount ? colors.nearBlack : colors.gray)};
`;

const Input = styled.input`
  ${globalInputStyles}
  padding: 5px 0 5px 30px;
  &:hover,
  &:focus {
    box-shadow: none;
  }
  text-align: left;
  border: none;
  width: 130px;
`;

const Line = styled.div`
  background-color: ${colors.lightGray};
  width: 1px;
  height: 40px;
`;

const Select = styled.select`
  font-size: 14px;
  font-weight: 500;
  outline: none;
  border: none;
  padding: 5px 0;
  background-color: transparent;
  flex: 2;
  padding-left: 0;
  text-align: right;
  height: 36px;
  color: ${colors.nearBlack};
  &:hover,
  &:focus {
    box-shadow: none;
  }
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const Option = styled.option``;

type Props = {
  title?: string;
  description?: string;
  type: "SPACE" | "DRINK" | "FOOD";
  value: Cost;
  onChange: (cost: Cost) => void;
};
const CostInput: React.FC<Props> = props => {
  const [focus, setFocus] = useState(false);

  return (
    <Outer>
      {props.title && <ItemTitle>{props.title}</ItemTitle>}
      {props.description && <ItemLabel>{props.description}</ItemLabel>}
      <Wrapper active={focus}>
        <Row>
          <MoneySign focus={focus} amount={props.value.amount}>
            $
          </MoneySign>
          <Input
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            value={props.value.amount ? props.value.amount : ""}
            onChange={(e: { target: { value: string } }) => {
              props.onChange({
                ...props.value,
                amount: Number(e.target.value)
              });
            }}
          />
        </Row>
        <Line />
        <Row>
          <Select
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            value={props.value.type}
            onChange={(e: { target: { value: string } }) => {
              props.onChange({
                ...props.value,
                type: e.target.value
              });
            }}
          >
            {props.type == "SPACE" &&
              SpaceCostTypes.map(costType => (
                <Option
                  key={`space-cost-type-option-${costType.name}`}
                  value={costType.name}
                >
                  {costType.title}
                </Option>
              ))}
            {props.type == "FOOD" &&
              FoodCostTypes.map(costType => (
                <Option
                  key={`food-cost-type-option-${costType.name}`}
                  value={costType.name}
                >
                  {costType.title}
                </Option>
              ))}
            {props.type == "DRINK" &&
              DrinkCostTypes.map(costType => (
                <Option
                  key={`drink-cost-type-option-${costType.name}`}
                  value={costType.name}
                >
                  {costType.title}
                </Option>
              ))}
          </Select>
        </Row>
      </Wrapper>
    </Outer>
  );
};
CostInput.defaultProps = {
  title: "Cost"
};

export default CostInput;
